/**
 * Safe CSS `url()` construction (audit finding F6.7 / SEC-5, gate G-51).
 *
 * Per-Segment wallpapers are stored in `ActivitySettings.bg_wallpaper`, an
 * unbounded TextField with no URL validator, and were interpolated into
 * styled-components as an UNQUOTED `url(${bgWallpaper})`. An unquoted CSS
 * `url()` token cannot contain whitespace, quotes, parentheses or control
 * characters — so a perfectly ordinary S3 key like
 *
 *     .../uploads/21/my photo (1).jpg
 *
 * silently produced an INVALID declaration and the background simply did not
 * render at all (the user's chosen wallpaper vanishes with no error), and a
 * hostile value such as
 *
 *     x.png);}body{display:none;}a{background:url(x.png
 *
 * escaped the declaration entirely (stored CSS injection).
 *
 * `cssUrl()` produces a QUOTED url token with the string escaped per the CSS
 * Syntax spec, which neutralises both problems: the value can no longer close
 * the token, and legal-but-unquotable characters (spaces, parens) survive.
 *
 * Spec: https://www.w3.org/TR/css-syntax-3/#consume-string-token
 */

/**
 * Escape a value for use inside a double-quoted CSS string token.
 * Backslashes and double quotes are escaped; newlines (which terminate a CSS
 * string token and would otherwise break out) are escaped as CSS newlines.
 *
 * @param {string} value
 * @returns {string}
 */
export function escapeCssString(value) {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"')
    .replace(/\r\n|\r|\n/g, "\\A ");
}

/**
 * Build a safe CSS `url("…")` token.
 *
 * Returns `"none"` for an empty / nullish / non-string value so the caller can
 * interpolate the result straight into `background-image` without emitting the
 * invalid `url()` that an empty wallpaper used to produce.
 *
 * @param {string|null|undefined} value the raw (user-controlled) URL
 * @returns {string} `url("…")` or `none`
 */
export function cssUrl(value) {
  if (typeof value !== "string") return "none";
  const trimmed = value.trim();
  if (!trimmed) return "none";
  return `url("${escapeCssString(trimmed)}")`;
}

export default cssUrl;
